import { useMemo } from 'react';
import { timeToMinutes, computeColumnLayouts } from '../../lib/timeline';
import type { ColumnLayout } from '../../lib/timeline';
import { usePlannerBlocks, usePlannerEvents } from '../../hooks/usePlannerData';
import { useAllTasks } from '../../hooks/useTasks';
import { PlannerColumnHeader } from './PlannerColumnHeader';
import { PlannerBlock } from './PlannerBlock';
import { PlannerMeetingBlock } from './PlannerMeetingBlock';
import { PlannerDayColumn } from './PlannerDayColumn';

const FOCUS_START_HOUR = 9;
const FOCUS_END_HOUR = 17;
const TIME_GUTTER_WIDTH = 48;

interface PlannerGridProps {
  weekDates: string[];
  startHour: number;
  endHour: number;
  pxPerHour: number;
  onBlockClick?: (taskId: string) => void;
}

export function PlannerGrid({ weekDates, startHour, endHour, pxPerHour, onBlockClick }: PlannerGridProps) {
  const blocksByDate = usePlannerBlocks(weekDates);
  const eventsByDate = usePlannerEvents(weekDates);
  const allTasks = useAllTasks();

  const taskMetaById = useMemo(
    () => new Map(allTasks.map((task) => [task.id, task])),
    [allTasks]
  );

  const hydratedBlocksByDate = useMemo(() => {
    const map = new Map<string, ReturnType<typeof blocksByDate.get>>();
    for (const date of weekDates) {
      const blocks = blocksByDate.get(date) ?? [];
      map.set(date, blocks.map((block) => {
        const task = taskMetaById.get(block.taskId);
        if (!task) return block;
        return {
          ...block,
          itemType: block.itemType ?? task.itemType,
          parentId: block.parentId ?? task.parentId ?? null,
        };
      }));
    }
    return map;
  }, [blocksByDate, taskMetaById, weekDates]);

  const allDayLayouts = useMemo(() => {
    const result = new Map<string, Map<string, ColumnLayout>>();
    for (const date of weekDates) {
      const blocks = hydratedBlocksByDate.get(date) ?? [];
      const events = eventsByDate.get(date) ?? [];
      const items = [
        ...blocks.map(b => ({
          id: b.id,
          startMin: timeToMinutes(b.scheduledStartTime),
          endMin: timeToMinutes(b.scheduledStartTime) + b.scheduledDuration,
        })),
        ...events.map(e => ({
          id: e.id,
          startMin: timeToMinutes(e.startTime),
          endMin: timeToMinutes(e.endTime),
        })),
      ];
      result.set(date, computeColumnLayouts(items));
    }
    return result;
  }, [hydratedBlocksByDate, eventsByDate, weekDates]);

  const totalHours = endHour - startHour;
  const gridHeight = totalHours * pxPerHour;

  const hourMarks: Array<{ hour: number; label: string }> = [];
  const firstVisibleHour = Number.isInteger(startHour) ? startHour : Math.ceil(startHour);
  for (let h = firstVisibleHour; h < endHour; h++) {
    const normalizedHour = ((h % 24) + 24) % 24;
    let displayHour = normalizedHour;
    const period = normalizedHour >= 12 ? 'PM' : 'AM';
    if (displayHour === 0) displayHour = 12;
    else if (displayHour > 12) displayHour -= 12;
    hourMarks.push({ hour: h, label: `${displayHour} ${period}` });
  }

  const hourToTop = (hour: number) => (hour - startHour) * pxPerHour;
  const focusStartTop = hourToTop(FOCUS_START_HOUR);
  const focusEndTop = hourToTop(FOCUS_END_HOUR);
  const showFocusHours = FOCUS_END_HOUR > startHour && FOCUS_START_HOUR < endHour;
  const clampedFocusTop = Math.max(0, focusStartTop);
  const clampedFocusBottom = Math.min(gridHeight, focusEndTop);

  return (
    <div className="flex flex-col min-h-0">
      {/* Column headers row */}
      <div
        data-planner-header
        className="grid shrink-0 sticky top-0 z-20 bg-bg-base border-b border-border-subtle/40"
        style={{ gridTemplateColumns: `${TIME_GUTTER_WIDTH}px repeat(7, 1fr)` }}
      >
        <div />
        {weekDates.map((date) => {
          const blocks = hydratedBlocksByDate.get(date) ?? [];
          const scheduledHours = blocks.reduce(
            (sum, b) => sum + b.scheduledDuration,
            0
          ) / 60;
          return (
            <PlannerColumnHeader key={date} date={date} scheduledHours={scheduledHours} />
          );
        })}
      </div>

      {/* Grid body */}
      <div
        className="grid relative"
        style={{
          gridTemplateColumns: `${TIME_GUTTER_WIDTH}px repeat(7, 1fr)`,
          height: gridHeight,
        }}
      >
        <div
          className="absolute top-0 bottom-0 border-l border-border-subtle/80 pointer-events-none"
          style={{ left: TIME_GUTTER_WIDTH }}
          aria-hidden="true"
        />

        {/* Left gutter with hour labels */}
        <div className="relative">
          {hourMarks.map(({ hour, label }) => (
            <span
              key={hour}
              className="absolute left-1 text-[10px] text-text-muted/55 font-mono select-none leading-none tracking-tight"
              style={{ top: hourToTop(hour) + 6 }}
            >
              {label}
            </span>
          ))}
        </div>

        {/* 7 day columns (droppable) */}
        {weekDates.map((date) => (
          <PlannerDayColumn
            key={date}
            date={date}
            startHour={startHour}
            endHour={endHour}
            gridHeight={gridHeight}
            pxPerHour={pxPerHour}
          >
            {(hydratedBlocksByDate.get(date) ?? []).map((block) => (
              <PlannerBlock key={block.id} block={block} startHour={startHour} pxPerHour={pxPerHour} columnLayout={allDayLayouts.get(date)?.get(block.id)} onBlockClick={onBlockClick} />
            ))}
            {(eventsByDate.get(date) ?? []).map((event) => (
              <PlannerMeetingBlock key={event.id} event={event} startHour={startHour} pxPerHour={pxPerHour} columnLayout={allDayLayouts.get(date)?.get(event.id)} />
            ))}
          </PlannerDayColumn>
        ))}

        {showFocusHours && clampedFocusBottom > clampedFocusTop && (
          <div
            className="absolute pointer-events-none"
            style={{ left: TIME_GUTTER_WIDTH, right: 0, top: clampedFocusTop, height: clampedFocusBottom - clampedFocusTop }}
            aria-hidden="true"
          >
            <div className="absolute left-0 right-0 top-0 border-t border-accent/25" />
            <div className="absolute left-0 right-0 bottom-0 border-t border-accent/25" />
          </div>
        )}

        {/* Hour lines spanning all 7 columns */}
        <div
          className="absolute pointer-events-none"
          style={{ left: 0, right: 0, top: 0, height: gridHeight }}
        >
          {hourMarks.map(({ hour }) => {
            const top = hourToTop(hour);
            const halfTop = top + pxPerHour / 2;
            return (
              <div key={hour}>
                <div
                  className="absolute left-0 right-0 border-t border-border-subtle/30"
                  style={{ top }}
                />
                <div
                  className="absolute left-0 border-t border-border-default/60"
                  style={{ top, width: TIME_GUTTER_WIDTH }}
                />
                {hour + 0.5 < endHour && (
                  <div
                    className="absolute left-0 right-0 border-t border-border-subtle/15"
                    style={{ top: halfTop }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
